/**
 * Draws the selected actor's motion path — the smoothed polyline plus draggable
 * waypoint handles.
 *
 * Display-only and `pointer-events-none`: all interaction lives in
 * `useStudioInteraction` via map events, so there is no event tug-of-war with
 * the map. Projection is imperative (scene feet -> lng/lat -> screen on every
 * camera move) with no per-frame React re-render, mirroring
 * `demo/masters-planner/components/TaxiEditorOverlay.tsx`.
 */
import { useEffect, useMemo, useRef } from 'react'
import { CoordinateService } from '@/services/3d/CoordinateService'
import { smoothPath, type Pt } from '@/demo/masters-planner/motion/easing'
import type { CinematicAircraft } from '../types'

interface Props {
  map: mapboxgl.Map | null
  /** Scene origin [lng, lat]. */
  center: [number, number] | null
  actor: CinematicAircraft | null
  selectedWaypointId: string | null
  visible: boolean
}

export function PathOverlay({ map, center, actor, selectedWaypointId, visible }: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const polyRef = useRef<SVGPolylineElement | null>(null)
  const nodeRefs = useRef<(SVGGElement | null)[]>([])

  const path = actor?.path ?? null

  // Densified line for display — matches what the motion sampler actually flies.
  const linePoints = useMemo<Pt[]>(() => {
    if (!path) return []
    const raw: Pt[] = path.waypoints.map((w) => [w.pos[0], w.pos[1]])
    return path.smooth && raw.length >= 3 ? smoothPath(raw, 8) : raw
  }, [path])

  useEffect(() => {
    if (!map || !center || !visible || !path) return

    const project = (pos: readonly [number, number]): [number, number] => {
      const ll = CoordinateService.scenePositionToLngLat([pos[0], 0, pos[1]], center)
      const p = map.project(ll as [number, number])
      return [p.x, p.y]
    }

    const update = () => {
      const poly = polyRef.current
      if (poly) {
        poly.setAttribute(
          'points',
          linePoints.map((p) => project(p).join(',')).join(' ')
        )
      }
      path.waypoints.forEach((wp, i) => {
        const g = nodeRefs.current[i]
        if (!g) return
        const [x, y] = project(wp.pos)
        g.setAttribute('transform', `translate(${x}, ${y})`)
      })
    }

    let raf = 0
    const schedule = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        update()
      })
    }

    map.on('move', schedule)
    update()
    return () => {
      map.off('move', schedule)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [map, center, visible, path, linePoints])

  if (!visible || !path || !actor) return null

  return (
    <svg
      ref={svgRef}
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ overflow: 'visible' }}
    >
      <polyline
        ref={polyRef}
        fill="none"
        stroke="#38bdf8"
        strokeWidth={2}
        strokeDasharray="6 4"
        strokeOpacity={0.85}
      />
      {path.waypoints.map((wp, i) => {
        const selected = wp.id === selectedWaypointId
        return (
          <g
            key={wp.id}
            ref={(el) => {
              nodeRefs.current[i] = el
            }}
          >
            <circle
              r={selected ? 7 : 5}
              fill={selected ? '#f0abfc' : '#0ea5e9'}
              stroke="#0f172a"
              strokeWidth={1.5}
            />
            {wp.holdMs > 0 && (
              <circle r={10} fill="none" stroke="#fbbf24" strokeWidth={1.5} strokeOpacity={0.8} />
            )}
            <text
              x={0}
              y={-12}
              textAnchor="middle"
              className="fill-white/70 text-[10px] font-semibold"
              style={{ paintOrder: 'stroke', stroke: '#0f172a', strokeWidth: 3 }}
            >
              {i + 1}
            </text>
          </g>
        )
      })}
    </svg>
  )
}
